import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Tab, Tabs, CircularProgress } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import Footer from "../../components/Reusable/Footer/Footer";
import { sections } from "../../constants/SectionDataDashboard";
import ActivityBar from "../../components/Reusable/ActivityBar/ActivityBar";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { apiService } from "../../api/apiService";
import DashboardContent from "./DashboardContent";
import { GraphDataItem, UploadData } from "./DashboardTypes";

interface ApiState {
  isLoading: boolean;
  error: string | null;
}

const Dashboard: React.FC = () => {
  const { instance } = useMsal();
  const [counts, setCounts] = useState({
    business_description_count: 0,
    process_map_count: 0,
    value_creation_count: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState("All Centers");

  const [graphData, setGraphData] = useState<GraphDataItem[]>([]);
  const [uploadData, setUploadData] = useState<UploadData>({});

  const [countsState, setCountsState] = useState<ApiState>({
    isLoading: false,
    error: null,
  });
  const [graphState, setGraphState] = useState<ApiState>({
    isLoading: false,
    error: null,
  });
  const [uploadsState, setUploadsState] = useState<ApiState>({
    isLoading: false,
    error: null,
  });

  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<{
    year: string;
    month: string;
  }>({ year: "", month: "" });

  const fetchCounts = async (center: string) => {
    setCountsState({ isLoading: true, error: null });
    try {
      const endpoint =
        center !== "All Centers"
          ? `/ops/db_pm_vc_counts?management=${encodeURIComponent(center)}`
          : "/ops/db_pm_vc_counts";

      const response = await apiService(
        endpoint,
        "GET",
        undefined,
        undefined,
        instance as PublicClientApplication
      );

      if (response?.status === "success") {
        setCounts(response.data);
      }
    } catch (err: any) {
      setCountsState((prev) => ({
        ...prev,
        error: err.message || "Error fetching counts",
      }));
      console.error("Counts API Error:", err);
    } finally {
      setCountsState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const fetchGraphData = async (center: string) => {
    setGraphState({ isLoading: true, error: null });
    try {
      const endpoint =
        center !== "All Centers"
          ? `/ops/evidence_statistics?management=${encodeURIComponent(center)}`
          : "/ops/evidence_statistics";

      const response = await apiService(
        endpoint,
        "GET",
        undefined,
        undefined,
        instance as PublicClientApplication
      );

      if (
        response?.status === "success" &&
        Array.isArray(response.data?.graph_data)
      ) {
        setGraphData(response.data.graph_data);
        if (response.data.graph_data.length > 0) {
          setSelectedSection(response.data.graph_data[0].section);
        }
      }
    } catch (err: any) {
      setGraphState((prev) => ({
        ...prev,
        error: err.message || "Error fetching graph data",
      }));
      console.error("Graph API Error:", err);
    } finally {
      setGraphState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const fetchUploads = async (center: string) => {
    setUploadsState({ isLoading: true, error: null });
    try {
      const endpoint =
        center !== "All Centers"
          ? `/ops/uploads_by_months?management=${encodeURIComponent(center)}`
          : "/ops/uploads_by_months";

      const response = await apiService(
        endpoint,
        "GET",
        undefined,
        undefined,
        instance as PublicClientApplication
      );

      if (response?.status === "success" && response.data) {
        setUploadData(response.data);
        if (Object.keys(response.data).length > 0) {
          const years = Object.keys(response.data).sort();
          const lastYear = years[years.length - 1];
          const months = Object.keys(response.data[lastYear]);
          const lastMonth = months[months.length - 1];
          setSelectedDate({ year: lastYear, month: lastMonth });
        }
      }
    } catch (err: any) {
      setUploadsState((prev) => ({
        ...prev,
        error: err.message || "Error fetching upload data",
      }));
      console.error("Uploads API Error:", err);
    } finally {
      setUploadsState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    const loadData = async (center: string) => {
      setIsLoading(true);
      setError(null);

      try {
        await Promise.all([
          fetchCounts(center),
          fetchGraphData(center),
          fetchUploads(center),
        ]);
      } catch (err: any) {
        setError(err.message || "Error loading dashboard data");
      } finally {
        setIsLoading(false);
      }
    };

    loadData("All Centers");
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    setIsLoading(true);

    Promise.all([
      fetchCounts(newValue),
      fetchGraphData(newValue),
      fetchUploads(newValue),
    ]).finally(() => {
      setIsLoading(false);
    });
  };

  const breadcrumbs = [
    <Link key="1" color="#000" to="/">
      Home
    </Link>,
    <Link key="2" color="#000" to="/">
      Dashboard
    </Link>,
  ];

  return (
    <>
      <TabContext value={selectedTab}>
        <ActivityBar breadcrumbs={breadcrumbs} />

        <Box sx={{ position: "sticky",         // 🔥 Make it sticky
    top: 0,                     // 🔝 Stick to top of viewport
    zIndex: 1,               // 📌 Above other content
    borderBottom: 1, borderColor: "divider", mx: 2, backgroundColor: "#fff", borderRadius: 1 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Dashboard tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: "Avantt",
                color: "#666",
                "&.Mui-selected": {
                  color: "#000",
                  fontWeight: 600,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#D7A833",
                height: 3,
              },
            }}
          >
            {sections.map((section) => (
              <Tab key={section} label={section} value={section} />
            ))}
          </Tabs>
        </Box>

        {sections.map((section) => (
          <TabPanel key={section} value={section} sx={{ p: '0px !important',minHeight: 'calc(100vh - 250px)' }}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box sx={{ color: "error.main", textAlign: "center", my: 4 }}>
                {error}
              </Box>
            ) : (
              <DashboardContent
                counts={counts}
                graphData={graphData}
                uploadData={uploadData}
                selectedSection={selectedSection}
                selectedDate={selectedDate}
                countsState={countsState}
                graphState={graphState}
                uploadsState={uploadsState}
              />
            )}
          </TabPanel>
        ))}

        <Box className="footer_dashboard">
          <Footer />
        </Box>
      </TabContext>
    </>
  );
};

export default Dashboard;
